import {
  getTimezoneItemsByLocale,
  utcZeroTimeToStandardTime,
  roundToDigitValue,
} from '@/common/utils/commonUtils';
import {
  CHECK_BY_COMBINATION,
  CHECK_BY_TARGET,
  getWeeklyScheduleItems,
  getCheckHoldTimeItems,
  getAlertTypeItems,
  getCheckIntervalItems,
  getAggregationItems,
  getEvaluateValueItems,
  getDuringTimeItems,
  ALERT_TYPE_VALUE,
  EVENT_VARIABLE_CONDITION_ITEMS,
  getEventTypeItems,
  getEventNameItems,
  EVENT_NAME_ENUM,
  EVENT_TYPE_ENUM,
} from '@/alert/utils/define';
import { SERVER_TAG_SEPARATOR } from '@/common/define/common.define';
import { getDisplayName } from '@/common/utils/core/string/displayName';
import { getDisplayNameFromSelectboxItem, parseLoggingValue } from '@/alert/utils/utils';
import { resolveDuringTimeIntervalKey } from '@/alert/utils/thresholdDisplay.utils';
import type { AlertValueFormatter } from '@/alert/components/alertGrid/alertGrid.types';
import {
  parseProcessDownPattern,
  convertEventType,
  getEventName,
} from '@/config/views/userAlert/alertRuleWindow.uses';
import type { EventType } from '@/config/views/userAlert/alertRuleWindow.types';
import { CATEGORIES as PROCESS_DOWN_CATEGORIES } from '@/config/views/userAlert/alertRuleDataConditions/alertRuleDataConditionEventProcess.define';
import type {
  EventAlertMessageItem,
  ResponseEventConditionV9,
  ResponseMetricConditionV9,
  ResponseSystemCondition,
  NotificationMethod,
  LoggingCondition,
  AlertFilter,
  AlertDisplayColumn,
} from '@/api-client/alert/model';
import { AlertRuleV9RequestSubType } from '@/api-client/metav8Over/model/alert-rule-v9-request-sub-type';
import { resolveAlertMetricSubType } from '@/alert/utils/alertMetricSubType.utils';
import { formatPostPeriod } from '../alertDetail/alertInformation.setup';
import type {
  RuleData,
  RuleViewData,
  ProcessedMetricInfo,
  RuleItem,
  RuleItemText,
  RuleItemList,
  RuleItemMultiText,
  OccurrenceItem,
  RuleGroup,
  EventAlertRuleDetail,
  ThresholdData,
} from './eventAlertInformation.types';
import { ALERT_CONTROL_TYPE_LABEL } from './eventAlertInformation.define';

// 알림 스케줄 라벨 변환 함수
export const toWeeklyScheduleLabels = (dayWeek?: string): string[] => {
  if (!dayWeek) return [];
  const items = getWeeklyScheduleItems();
  const order = items.map((i) => i.name);
  const values = dayWeek
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean);
  const names = values.map((v) => getDisplayNameFromSelectboxItem(items, v));
  return names.sort((a, b) => order.indexOf(a) - order.indexOf(b));
};

// 시간 범위 포맷 함수
export const formatTimeRange = (start: string | undefined, end: string | undefined): string => {
  const format = (s?: string) => {
    if (!s) return '';
    const m = s.match(/\b(\d{2}):(\d{2})\b/);
    return m ? `${m[1]}:${m[2]}` : s;
  };
  const s = format(start);
  const e = format(end);
  if (!s && !e) return '';
  if (!s) return e;
  if (!e) return s;
  return `${s} ~ ${e}`;
};

// 알람 제어 시간 포맷 함수
export const formatHold = (
  t: (key: string) => string,
  hold: string | undefined,
  alertMode: string | undefined,
): string => {
  if (hold === '-1') {
    return t('WORD.NONE_2');
  }

  const items = getCheckHoldTimeItems();
  const alertControlType = ALERT_CONTROL_TYPE_LABEL[alertMode ?? ''];

  if (!alertControlType) {
    return `${getDisplayNameFromSelectboxItem(items, hold ?? '')}`;
  }

  return `${t(alertControlType)} (${getDisplayNameFromSelectboxItem(items, hold ?? '')})`;
};

export const formatValue = (value: unknown): string => {
  if (!value) return '';

  const isNumeric =
    typeof value === 'number' ||
    (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)));

  if (!isNumeric) return String(value);

  const rounded = roundToDigitValue(value as string | number, 2);
  return String(rounded ?? value);
};

// ============================================================================
// 공통 유틸리티 함수
// ============================================================================

// 태그 키에서 SERVER_TAG_SEPARATOR 제거
const removeServerTagSeparator = (tagKey: string): string => {
  if (tagKey.startsWith(SERVER_TAG_SEPARATOR)) {
    return tagKey.substring(SERVER_TAG_SEPARATOR.length);
  }
  return tagKey;
};

// 값이 유효한지 검증
const isPresent = (v: unknown): v is string | number => v !== undefined && v !== null && v !== '';

// 태그 데이터 변환
const convertTagToDisplayFormat = (tag: {
  tagId?: string;
  tagKey?: string;
  tagName?: string;
  tagType?: string;
}): { id: string; name: string; category: string } => {
  const displayTagKey = removeServerTagSeparator(tag.tagKey ?? '');
  return {
    id: tag.tagId ?? '',
    name: tag.tagId === '*' ? `${displayTagKey}: *` : `${displayTagKey}: ${tag.tagName ?? ''}`,
    category: tag.tagType ?? '',
  };
};

// Process Down Pattern 포맷팅
const getFormalPattern = (item: string) => {
  const { category, dataType, pattern } = parseProcessDownPattern(item);

  const categoryForUi = getDisplayNameFromSelectboxItem(PROCESS_DOWN_CATEGORIES, category);

  if (dataType === 'string') {
    return `${categoryForUi} like %${pattern}%`;
  }
  if (dataType === 'regex') {
    return `${categoryForUi} ${pattern}`;
  }

  return '';
};

// 타겟 데이터 변환
const convertTargetToDisplayFormat = (target: {
  targetId?: string;
  category?: string;
  targetName?: string;
  targetAlias?: string;
}): { id: string; name: string; category: string } => ({
  id: target.targetId ?? '',
  name: `${target.category ?? ''}: ${getDisplayName(target.targetName ?? '', target.targetAlias)}`,
  category: target.category ?? '',
});

type ThresholdSource = {
  evaluateValue?: string;
  duringTimeInterval?: string;
  criticalComparison?: string;
  warningComparison?: string;
  warning?: number;
  critical?: number;
};

/** 이벤트 상세 metric ↔ 룰 metric 매칭 (index 불일치 시 conditionId로 보완) */
export const findRuleMetricThreshold = (
  metric: ResponseMetricConditionV9,
  ruleMetricConditions: ResponseMetricConditionV9[],
  index: number,
): ResponseMetricConditionV9['threshold'] | undefined => {
  if (metric.conditionId) {
    const matched = ruleMetricConditions.find((c) => c.conditionId === metric.conditionId);
    if (matched?.threshold) {
      return matched.threshold;
    }
  }
  return ruleMetricConditions[index]?.threshold;
};

// Threshold 데이터 변환 (이벤트 상세 응답에 duringTimeInterval이 없을 때 룰 정의값으로 보완)
const convertThresholdData = (
  threshold: ThresholdSource,
  ruleThreshold?: ThresholdSource,
): {
  evaluateValue: string;
  duringTimeInterval: string;
  comparator: string;
  warningValue?: number;
  criticalValue?: number;
} => {
  const duringTimeIntervalKey = resolveDuringTimeIntervalKey(threshold, ruleThreshold);

  return {
    evaluateValue: threshold.evaluateValue
      ? getDisplayNameFromSelectboxItem(getEvaluateValueItems(), threshold.evaluateValue)
      : '',
    duringTimeInterval: duringTimeIntervalKey
      ? getDisplayNameFromSelectboxItem(getDuringTimeItems(), duringTimeIntervalKey)
      : '',
    comparator: threshold.criticalComparison ?? threshold.warningComparison ?? '',
    warningValue: threshold.warning,
    criticalValue: threshold.critical,
  };
};

// API 응답 데이터를 RuleData 타입으로 변환하는 함수
export const convertApiToRuleData = (api: EventAlertRuleDetail): RuleData => {
  const rawSubType = 'subType' in api && typeof api.subType === 'string' ? api.subType : undefined;
  const rawEventType = api.eventConditions?.[0]?.eventType;
  let convertedEventType = rawEventType ? convertEventType.forUi(rawEventType) : undefined;
  const variableCondition = api.eventConditions?.[0]?.variableConditionList?.[0];
  const convertedEventName = Object.values(EVENT_NAME_ENUM).includes(rawEventType ?? '')
    ? rawEventType
    : undefined;
  const eventName = variableCondition ? getEventName(variableCondition) : undefined;

  // Kubernetes 이벤트의 경우 resourceStatusType 추출
  let resourceStatusType: string | undefined;
  if (
    convertedEventType === EVENT_TYPE_ENUM.KUBERNETES ||
    convertedEventType === EVENT_TYPE_ENUM.KUBERNETES_PHASE
  ) {
    const statusTypes = variableCondition?.resourceStatusType;
    resourceStatusType =
      Array.isArray(statusTypes) && statusTypes.length > 0 ? statusTypes[0] : undefined;

    // Kubernetes Phase는 이벤트 타입을 Kubernetes로 통일
    if (convertedEventType === EVENT_TYPE_ENUM.KUBERNETES_PHASE) {
      convertedEventType = EVENT_TYPE_ENUM.KUBERNETES;
    }
  }

  return {
    alertRuleName: api.alertRuleName ?? '',
    alertRuleType: api.alertRuleType ?? '',
    subType: resolveAlertMetricSubType({
      subType: rawSubType,
      ruleCriteria: api.ruleCriteria,
      metricConditions: api.metricConditions,
    }),
    ruleCriteria: api.ruleCriteria ?? '',
    checkInterval: api.checkInterval ?? 0,
    checkRepeat: api.checkRepeat ?? 0,
    maximumOccurrences: api.maximumOccurrences ?? 0,
    checkHoldTime: api.checkHoldTime ?? 'p0s',
    alertMode: api.alertMode ?? undefined,
    weeklySchedule: {
      timeZone: api.weeklySchedule?.timeZone ?? '',
      dayWeek: api.weeklySchedule?.dayWeek ?? '',
      startTime: api.weeklySchedule?.startTime ?? '',
      endTime: api.weeklySchedule?.endTime ?? '',
    },
    eventType: convertedEventType,
    event: eventName || convertedEventName,
    resourceStatusType,
    platform: api.eventConditions?.[0]?.category,
    loggingCondition: api.eventConditions?.[0]?.loggingCondition,
    duringTimeInterval: api.eventConditions?.[0]?.duringTimeInterval,
  };
};

// 룰 API 데이터를 뷰 데이터로 변환하는 함수
export const convertRuleApiToView = (api: RuleData, t: (key: string) => string): RuleViewData => ({
  type: api.alertRuleType,
  typeLabel:
    getDisplayNameFromSelectboxItem(
      getAlertTypeItems({ containExternal: true }),
      api.alertRuleType,
    ) || api.alertRuleType,
  subType: api.subType,
  ruleCriteria: api.ruleCriteria,
  checkInterval: api.checkInterval,
  checkIntervalLabel: `${api.checkInterval} ${getDisplayNameFromSelectboxItem(
    getCheckIntervalItems(),
    's',
  )}`,
  checkRepeat: api.checkRepeat,
  maximumOccurrences: api.maximumOccurrences,
  checkHoldTime: formatHold(t, api.checkHoldTime, api.alertMode),
  weeklySchedule: toWeeklyScheduleLabels(api.weeklySchedule?.dayWeek),
  timeZone:
    getDisplayNameFromSelectboxItem(
      getTimezoneItemsByLocale(),
      api.weeklySchedule?.timeZone ?? '',
    ) ||
    api.weeklySchedule?.timeZone ||
    '',
  timeRange: formatTimeRange(api.weeklySchedule?.startTime, api.weeklySchedule?.endTime),
  eventType: api.eventType,
  eventTypeLabel: api.eventType
    ? getDisplayNameFromSelectboxItem(getEventTypeItems(), api.eventType)
    : undefined,
  event: api.event,
  eventLabel:
    api.eventType && api.event
      ? getDisplayNameFromSelectboxItem(getEventNameItems(api.eventType as EventType), api.event)
      : undefined,
  resourceStatusType: api.resourceStatusType,
  resourceStatusTypeLabel: api.resourceStatusType
    ? getDisplayNameFromSelectboxItem(
        EVENT_VARIABLE_CONDITION_ITEMS.resourceStatusType,
        api.resourceStatusType,
      )
    : undefined,
  platform: api.platform,
  duringTimeInterval: api.duringTimeInterval
    ? getDisplayNameFromSelectboxItem(getDuringTimeItems(), api.duringTimeInterval)
    : '',
});

// 메트릭 정보 변환 함수 (API 응답 기반)
export const convertMetricInfo = (
  metric: ResponseMetricConditionV9,
  ruleThreshold?: ResponseMetricConditionV9['threshold'],
): ProcessedMetricInfo => {
  const { threshold } = metric;
  const aggregation = threshold?.aggregation
    ? getDisplayNameFromSelectboxItem(getAggregationItems(), threshold.aggregation)
    : '';

  const thresholdData = threshold ? convertThresholdData(threshold, ruleThreshold) : null;

  return {
    category: metric.category ?? '',
    alertStatName: threshold?.alertName ?? '',
    aggregation,
    formulaAlias: threshold?.formulaAlias ?? '',
    evaluateValue: thresholdData?.evaluateValue ?? '',
    duringTimeInterval: thresholdData?.duringTimeInterval ?? '',
    comparator: thresholdData?.comparator ?? '',
    warningValue: thresholdData?.warningValue,
    criticalValue: thresholdData?.criticalValue,
    unit: '',
    alertLevel: metric.alertLevel as 'warning' | 'critical',
    alertValue: formatValue(metric.alertValue),
    conditionType: threshold?.conditionType,
    tags:
      metric.tags
        ?.filter((tag) => tag.tagName || tag.tagId === '*')
        .map(convertTagToDisplayFormat) ?? [],
    targets:
      metric.targets
        ?.filter((target) => target.targetName || target.targetAlias)
        .map(convertTargetToDisplayFormat) ?? [],
  };
};

// 타입 가드 함수들
export const isTextItem = (item: RuleItem): item is RuleItemText =>
  item.kind === 'text' || item.kind === 'labelChip';

export const isListItem = (item: RuleItem): item is RuleItemList => item.kind === 'list';

export const isMultiTextItem = (item: RuleItem): item is RuleItemMultiText =>
  item.kind === 'multiText';

// 발생 메시지 아이템 타입 가드 함수들
export const isTextOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'text' } => item.type === 'text';

export const isListOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'list' } => item.type === 'list';

export const isStatusOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'status' } => item.type === 'status';

export const isNotificationStatusOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'notificationStatus' } => item.type === 'notificationStatus';

export const isFilterConditionsOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'filterConditions' } => item.type === 'filterConditions';

export const isLogViewerOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'logViewer' } => item.type === 'logViewer';

export const isTagListOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'tagList' } => item.type === 'tagList';

export const isTargetListOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'targetList' } => item.type === 'targetList';

export const isThresholdOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'threshold' } => item.type === 'threshold';

export const isAlertFilterOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'alertFilter' } => item.type === 'alertFilter';

export const isDisplayColumnsOccurrenceItem = (
  item: OccurrenceItem,
): item is OccurrenceItem & { type: 'displayColumns' } => item.type === 'displayColumns';

// ============================================================================
// 룰 아이템 빌더 헬퍼 함수
// ============================================================================

const createRuleItemHelpers = () => {
  const addText = (label: string, value: unknown, kind: 'text' | 'labelChip' = 'text') => {
    if (isPresent(value)) {
      return { label, kind, value: value as string | number } as RuleItemText;
    }
    return undefined;
  };

  const addList = (label: string, list?: string[]) => {
    if (Array.isArray(list) && list.length > 0) {
      return { label, kind: 'list', list } as RuleItemList;
    }
    return undefined;
  };

  const addMultiText = (label: string, texts: (string | number | undefined)[]) => {
    const filtered = texts.filter(isPresent);
    if (filtered.length > 0) {
      return { label, kind: 'multiText', texts: filtered } as RuleItemMultiText;
    }
    return undefined;
  };

  return { addText, addList, addMultiText };
};

// 룰 그룹 생성 함수
export const buildRuleGroups = (
  ruleViewData: RuleViewData,
  t: (key: string) => string,
  metricConditions?: ResponseMetricConditionV9[],
): RuleGroup[] => {
  const groups: RuleGroup[] = [];
  const { addText, addList, addMultiText } = createRuleItemHelpers();

  // Group 1: Type, Rule Criteria, Metric Type (METRIC 타입일 때)
  const basicInfoItems: RuleItem[] = [];
  const typeItem = addText(t('WORD.TYPE'), ruleViewData.typeLabel, 'text');
  const criteriaItem = addText(t('WORD.RULE_CRITERIA'), ruleViewData.ruleCriteria, 'labelChip');
  if (typeItem) basicInfoItems.push(typeItem);
  if (criteriaItem) basicInfoItems.push(criteriaItem);

  if (basicInfoItems.length) groups.push({ items: basicInfoItems });

  const alertType = ruleViewData.type;

  // System 타입이 아닐 때만 Group 2, Group 3 표시
  if (alertType !== ALERT_TYPE_VALUE.SYSTEM) {
    // Group 2: Check interval/repeat/maximum/hold
    const checkConfigItems: RuleItem[] = [];

    // Kubernetes 이벤트일 때 duringTimeInterval을 Group 2 맨 앞에 표시
    if (ruleViewData.eventType === EVENT_TYPE_ENUM.KUBERNETES) {
      const duringTimeItem = addText(
        t('WORD.AGGREGATION_SCOPE'),
        ruleViewData.duringTimeInterval,
        'text',
      );
      if (duringTimeItem) checkConfigItems.push(duringTimeItem);
    }

    // 프로세스 상태 감지 / 프로세스 수 변동 감지 이벤트일 때 집계 범위 '설정 안 함' 표시
    if (
      ruleViewData.event === EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION ||
      ruleViewData.event === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION
    ) {
      const aggregationItem = addText(t('WORD.AGGREGATION_SCOPE'), t('WORD.NONE_2'), 'text');
      if (aggregationItem) checkConfigItems.push(aggregationItem);
    }

    // EVENT 타입이 아닐 때만 checkInterval 표시
    if (alertType !== ALERT_TYPE_VALUE.EVENT) {
      const intervalItem = addText(
        t('WORD.CHECK_INTERVAL'),
        ruleViewData.checkIntervalLabel,
        'text',
      );
      if (intervalItem) checkConfigItems.push(intervalItem);
    }

    // 이벤트/이벤트 통계 타입은 룰 설정 화면과 동일하게 '이벤트 발생 횟수'로 표기 (같은 checkRepeat 값)
    const repeatLabel =
      alertType === ALERT_TYPE_VALUE.EVENT || alertType === ALERT_TYPE_VALUE.EVENT_STATISTICS
        ? t('WORD.EVENT_NUMBER_OF_OCCURRENCES')
        : t('WORD.CHECK_REPEAT');
    const repeatItem = addText(repeatLabel, ruleViewData.checkRepeat, 'text');
    if (repeatItem) checkConfigItems.push(repeatItem);

    // EVENT_STATISTICS 타입이 아닐 때만 maximumOccurrences 표시
    if (alertType !== ALERT_TYPE_VALUE.EVENT_STATISTICS) {
      const maxOccurrencesItem = addText(
        t('WORD.MAXIMUM_OCCURRENCES'),
        ruleViewData.maximumOccurrences === -1 ? '∞' : ruleViewData.maximumOccurrences,
        'text',
      );
      if (maxOccurrencesItem) checkConfigItems.push(maxOccurrencesItem);
    }

    // EVENT_STATISTICS 타입이 아닐 때만 checkHoldTime 표시
    if (alertType !== ALERT_TYPE_VALUE.EVENT_STATISTICS) {
      const holdTimeItem = addText(
        t('WORD.ALARM_CONTROL_DURATION'),
        ruleViewData.checkHoldTime,
        'text',
      );
      if (holdTimeItem) checkConfigItems.push(holdTimeItem);
    }

    if (checkConfigItems.length) groups.push({ items: checkConfigItems });

    // Group 3: Alert Schedule, time zone + range
    const scheduleItems: RuleItem[] = [];
    const weeklyScheduleItem = addList(t('WORD.ALERT_CHECK_SCHEDULE'), ruleViewData.weeklySchedule);
    if (weeklyScheduleItem) scheduleItems.push(weeklyScheduleItem);

    // EVENT_STATISTICS 타입일 때는 라벨 변경
    const timeLabel =
      alertType === ALERT_TYPE_VALUE.EVENT_STATISTICS ? t('WORD.CHECK_TIME2') : t('WORD.TIME');
    const timeItem = addMultiText(timeLabel, [ruleViewData.timeZone, ruleViewData.timeRange]);
    if (timeItem) scheduleItems.push(timeItem);

    if (scheduleItems.length) groups.push({ items: scheduleItems });
  }

  // Group 4: Event type + Event or Resource Status Type (for Kubernetes)
  const eventTypeItems: RuleItem[] = [];
  const eventTypeItem = addText(
    t('WORD.EVENT_TYPE'),
    ruleViewData.eventTypeLabel || ruleViewData.eventType,
    'text',
  );
  if (eventTypeItem) eventTypeItems.push(eventTypeItem);

  // Kubernetes 타입인 경우 Resource Status Type 표시, 그 외에는 Event 표시
  const isKubernetesType = ruleViewData.eventType === EVENT_TYPE_ENUM.KUBERNETES;

  if (isKubernetesType) {
    // Kubernetes: Resource Status Type 표시
    if (ruleViewData.resourceStatusTypeLabel || ruleViewData.resourceStatusType) {
      const resourceStatusItem = addText(
        t('WORD.RESOURCE_STATUS_TYPE'),
        ruleViewData.resourceStatusTypeLabel || ruleViewData.resourceStatusType,
        'text',
      );
      if (resourceStatusItem) eventTypeItems.push(resourceStatusItem);
    }
  } else if (ruleViewData.eventLabel || ruleViewData.event) {
    // 일반 이벤트: Event 표시
    const eventItem = addText(
      t('WORD.EVENT'),
      ruleViewData.eventLabel || ruleViewData.event,
      'text',
    );
    if (eventItem) eventTypeItems.push(eventItem);
  }

  if (eventTypeItems.length) groups.push({ items: eventTypeItems });

  // Group 5: Platform (System 타입일 때만 표시)
  if (alertType === ALERT_TYPE_VALUE.SYSTEM) {
    const platformItems: RuleItem[] = [];
    const platformItem = addText(t('WORD.PLATFORM'), ruleViewData.platform, 'text');
    if (platformItem) platformItems.push(platformItem);
    if (platformItems.length) groups.push({ items: platformItems });
  }

  // Group 6: 메트릭 타입 (METRIC 타입일 때만 표시, 마지막 행)
  if (alertType === ALERT_TYPE_VALUE.METRIC && metricConditions) {
    const isCombination = ruleViewData.ruleCriteria === CHECK_BY_COMBINATION;
    const resolved = resolveAlertMetricSubType({
      subType: ruleViewData.subType,
      ruleCriteria: ruleViewData.ruleCriteria,
      metricConditions,
    });
    const metricTypeMap: Partial<Record<AlertRuleV9RequestSubType, string>> = {
      [AlertRuleV9RequestSubType.CUSTOM]: t('WORD.CUSTOM'),
      [AlertRuleV9RequestSubType.COMPOUND]: t('WORD.MULTI_ALERT'),
      [AlertRuleV9RequestSubType.SINGLE]: t('WORD.SINGLE_ALERT'),
    };
    const metricType = isCombination || !resolved ? undefined : metricTypeMap[resolved];
    const metricTypeItem = addText(t('WORD.METRIC_TYPE'), metricType, 'labelChip');
    if (metricTypeItem) groups.push({ items: [metricTypeItem] });
  }

  return groups;
};

// ============================================================================
// Occurrence 아이템 생성 헬퍼 함수
// ============================================================================

const addOccurrenceItem = (items: OccurrenceItem[], item: OccurrenceItem | null): void => {
  if (item) items.push(item);
};

const createTextItem = (label: string, value: unknown): OccurrenceItem | null => {
  return isPresent(value) ? { label, type: 'text', value: value as string | number } : null;
};

const createListItem = (
  label: string,
  list?: string[],
  listRenderType: 'tags' | 'separator' = 'tags',
  labelBadge?: string,
): OccurrenceItem | null => {
  return list && list.length > 0 ? { label, type: 'list', list, listRenderType, labelBadge } : null;
};

const createTagListItem = (
  label: string,
  tags?: Array<{ id: string; name: string; category: string }>,
): OccurrenceItem | null => {
  return tags && tags.length > 0 ? { label, type: 'tagList', tags } : null;
};

const createTargetListItem = (
  label: string,
  targets?: Array<{ id: string; name: string; category: string }>,
): OccurrenceItem | null => {
  return targets && targets.length > 0 ? { label, type: 'targetList', targets } : null;
};

const createThresholdItem = (label: string, threshold: ThresholdData): OccurrenceItem | null => {
  const hasThreshold = isPresent(threshold.warningValue) || isPresent(threshold.criticalValue);
  return hasThreshold ? { label, type: 'threshold', threshold } : null;
};

const createStatusItem = (label: string, value?: string): OccurrenceItem | null => {
  if (!value) return null;
  const lowerValue = value.toLowerCase();
  let statusType: 'warning' | 'critical' | 'normal' = 'normal';

  if (lowerValue === 'critical') {
    statusType = 'critical';
  } else if (lowerValue === 'warning') {
    statusType = 'warning';
  }

  return { label, type: 'status', statusType, value };
};

const createNotificationStatusItem = (
  label: string,
  notificationStatus?: NotificationMethod[],
): OccurrenceItem | null => {
  return notificationStatus && notificationStatus.length > 0
    ? { label, type: 'notificationStatus', notificationStatus }
    : null;
};

const createLogViewerItem = (label: string, value: unknown): OccurrenceItem | null => {
  return isPresent(value) ? { label, type: 'logViewer', value: value as string } : null;
};

const createAlertFilterItem = (label: string, alertFilter?: AlertFilter): OccurrenceItem | null => {
  return alertFilter && alertFilter.items && alertFilter.items.length > 0
    ? { label, type: 'alertFilter', alertFilter }
    : null;
};

const createDisplayColumnsItem = (
  label: string,
  displayColumns?: AlertDisplayColumn[],
): OccurrenceItem | null => {
  if (!displayColumns || displayColumns.length === 0) return null;
  const visibleColumns = displayColumns.filter((col) => !col.hidden);
  return visibleColumns.length > 0
    ? { label, type: 'displayColumns', displayColumns: visibleColumns }
    : null;
};

// 공통 occurrence 아이템 추가
const addCommonOccurrenceItems = (
  items: OccurrenceItem[],
  messageItem: EventAlertMessageItem | undefined,
  t: (key: string) => string,
): void => {
  addOccurrenceItem(items, createTextItem(t('WORD.RULE_NAME'), messageItem?.alertRuleName));
  addOccurrenceItem(
    items,
    createTextItem(
      t('WORD.ALERT_TRIGGER_TIME'),
      utcZeroTimeToStandardTime(messageItem?.triggerTime),
    ),
  );
};

// 이벤트 조건 occurrence 아이템 추가
const addEventConditionItems = (
  items: OccurrenceItem[],
  eventCondition: ResponseEventConditionV9,
  t: (key: string) => string,
  alertRuleType?: string,
  getReasons?: (eventIds: number[]) => string[],
  notificationStatus?: NotificationMethod[],
  getAlertValue?: AlertValueFormatter,
): void => {
  const variableCondition = eventCondition.variableConditionList?.[0];

  addOccurrenceItem(
    items,
    createTagListItem(
      t('WORD.TAG'),
      eventCondition.tags
        ?.filter((tag) => tag.tagName || tag.tagId === '*')
        .map(convertTagToDisplayFormat),
    ),
  );
  addOccurrenceItem(
    items,
    createTargetListItem(
      t('WORD.TARGET'),
      eventCondition.targets
        ?.filter((target) => target.targetName || target.targetAlias)
        .map(convertTargetToDisplayFormat),
    ),
  );
  addOccurrenceItem(
    items,
    createNotificationStatusItem(t('WORD.NOTIFICATION_STATUS'), notificationStatus),
  );

  if (eventCondition.eventType === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION) {
    // threshold 필드는 응답 모델에 미정의(개발자 변경이슈로 condition에 추가됨) — 룰 상세와 동일하게 cast
    const threshold = eventCondition as unknown as {
      comparison?: string;
      warningThreshold?: number | null;
      criticalThreshold?: number | null;
    };
    items.push({
      label: t('WORD.DETECTION_PATTERNS'),
      type: 'list',
      list: (variableCondition?.detectedPattern ?? []).map(getFormalPattern),
      listRenderType: 'tags',
      labelBadge: 'AND',
    });
    items.push({
      label: t('WORD.EXCLUDED_PATTERNS'),
      type: 'list',
      list: (variableCondition?.excludePattern ?? []).map(getFormalPattern),
      listRenderType: 'tags',
      labelBadge: 'OR',
    });
    addOccurrenceItem(
      items,
      createTextItem(
        t('WORD.ALERT_POSTING_PERIOD'),
        formatPostPeriod(eventCondition.postPeriod ?? null),
      ),
    );
    addOccurrenceItem(
      items,
      createThresholdItem(t('WORD.THRESHOLD'), {
        evaluateValue: '',
        duringTimeInterval: '',
        comparator: threshold.comparison ?? '',
        warningValue: threshold.warningThreshold ?? undefined,
        criticalValue: threshold.criticalThreshold ?? undefined,
        unit: 'Count',
      }),
    );
    addOccurrenceItem(
      items,
      createTextItem(t('WORD.ALERT_VALUE'), `${formatValue(eventCondition.alertValue)} Count`),
    );
    return;
  }

  addOccurrenceItem(items, createStatusItem(t('WORD.ALERT_LEVEL'), eventCondition.level));

  if (alertRuleType === ALERT_TYPE_VALUE.EVENT) {
    let formattedValue: unknown = eventCondition.alertValue;

    if (eventCondition.eventType) {
      if (eventCondition.eventType === EVENT_TYPE_ENUM.LOGGING) {
        formattedValue = parseLoggingValue(String(eventCondition.alertValue));
      } else if (getAlertValue) {
        formattedValue = getAlertValue(String(eventCondition.alertValue), {
          eventType: eventCondition.eventType,
        });
      }
    }

    addOccurrenceItem(items, createLogViewerItem(t('WORD.ALERT_VALUE'), formattedValue));
  } else {
    addOccurrenceItem(
      items,
      createTextItem(t('WORD.ALERT_VALUE'), formatValue(eventCondition.alertValue)),
    );
  }

  addOccurrenceItem(items, createTextItem('Message Text', eventCondition.messageText));

  if (eventCondition.loggingCondition) {
    addOccurrenceItem(
      items,
      createTextItem(t('WORD.LOGGING'), eventCondition.loggingCondition.loggingName),
    );
  }

  if (variableCondition) {
    // Kubernetes 이벤트 특화 정보
    if (variableCondition?.kind?.[0]) {
      const kindDisplayName = getDisplayNameFromSelectboxItem(
        EVENT_VARIABLE_CONDITION_ITEMS.k8sKind,
        variableCondition.kind[0],
      );
      addOccurrenceItem(items, createTextItem('Kind', kindDisplayName));
    }

    if (variableCondition?.status && variableCondition.status.length > 0) {
      addOccurrenceItem(
        items,
        createListItem(t('WORD.PHASE_STATUS'), variableCondition.status, 'tags'),
      );
    }

    if (variableCondition?.eventId && getReasons) {
      const eventIds = variableCondition.eventId.map((id) => Number(id));
      const reasons = getReasons(eventIds);
      addOccurrenceItem(items, createListItem('Reason', reasons, 'tags'));
    }

    // 프로세스 상태 감지 이벤트: 감지 패턴/제외 패턴 행을 항상 표시 (빈 값은 빈 태그영역)
    if (eventCondition.eventType === EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION) {
      items.push({
        label: t('WORD.DETECTION_PATTERNS'),
        type: 'list',
        list: (variableCondition.detectedPattern ?? []).map(getFormalPattern),
        listRenderType: 'tags',
        labelBadge: 'AND',
      });
      items.push({
        label: t('WORD.EXCLUDED_PATTERNS'),
        type: 'list',
        list: (variableCondition.excludePattern ?? []).map(getFormalPattern),
        listRenderType: 'tags',
        labelBadge: 'OR',
      });
    }

    // Network Syslog 이벤트 특화 정보
    if (variableCondition.facility && variableCondition.facility.length > 0) {
      const facilityList = variableCondition.facility.map((facilityValue) =>
        getDisplayNameFromSelectboxItem(EVENT_VARIABLE_CONDITION_ITEMS.facility, facilityValue),
      );
      addOccurrenceItem(items, createListItem('Facility', facilityList, 'tags'));
    }
    if (variableCondition.severity && variableCondition.severity.length > 0) {
      const severityList = variableCondition.severity.map((severityValue) =>
        getDisplayNameFromSelectboxItem(EVENT_VARIABLE_CONDITION_ITEMS.severity, severityValue),
      );
      addOccurrenceItem(items, createListItem('Severity', severityList, 'tags'));
    }

    // Network Trap 이벤트 특화 정보
    if (variableCondition.genericTrapType && variableCondition.genericTrapType.length > 0) {
      const genericTrapTypeList = variableCondition.genericTrapType.map((genericTrapTypeValue) =>
        getDisplayNameFromSelectboxItem(
          EVENT_VARIABLE_CONDITION_ITEMS.genericTrapType,
          genericTrapTypeValue,
        ),
      );
      addOccurrenceItem(items, createListItem('Generic Trap Type', genericTrapTypeList, 'tags'));
    }
    if (variableCondition.specificTrapCode && variableCondition.specificTrapCode.length > 0) {
      addOccurrenceItem(
        items,
        createListItem('Specific Trap Code', variableCondition.specificTrapCode, 'tags'),
      );
    }
    if (variableCondition.enterpriseOid && variableCondition.enterpriseOid.length > 0) {
      addOccurrenceItem(
        items,
        createListItem('Enterprise OID', variableCondition.enterpriseOid, 'tags'),
      );
    }
  }

  addOccurrenceItem(
    items,
    createTextItem(
      t('WORD.ALERT_POSTING_PERIOD'),
      formatPostPeriod(eventCondition.postPeriod ?? null),
    ),
  );
};

// System 조건 occurrence 아이템 추가
const addSystemConditionItems = (
  items: OccurrenceItem[],
  systemCondition: ResponseSystemCondition,
  t: (key: string) => string,
  notificationStatus?: NotificationMethod[],
): void => {
  addOccurrenceItem(
    items,
    createTagListItem(
      t('WORD.TAG'),
      systemCondition.tags
        ?.filter((tag) => tag.tagName || tag.tagId === '*')
        .map(convertTagToDisplayFormat),
    ),
  );
  addOccurrenceItem(
    items,
    createTargetListItem(
      t('WORD.TARGET'),
      systemCondition.targets
        ?.filter((target) => target.targetName || target.targetAlias)
        .map(convertTargetToDisplayFormat),
    ),
  );
  addOccurrenceItem(
    items,
    createNotificationStatusItem(t('WORD.NOTIFICATION_STATUS'), notificationStatus),
  );
  addOccurrenceItem(items, createStatusItem(t('WORD.ALERT_LEVEL'), systemCondition.level));
};

// 메트릭 조건 occurrence 아이템 추가
const addMetricConditionItems = (
  items: OccurrenceItem[],
  metricCondition: ResponseMetricConditionV9,
  t: (key: string) => string,
  alertRuleType?: string,
  ruleCriteria?: string,
  getStatName?: (category: string, statId: string) => string,
  notificationStatus?: NotificationMethod[],
): void => {
  const isMetricType = alertRuleType === ALERT_TYPE_VALUE.METRIC;
  const isTableType = alertRuleType === ALERT_TYPE_VALUE.TABLE;
  const isCheckByTarget = ruleCriteria === CHECK_BY_TARGET;

  if ((isMetricType && isCheckByTarget) || isTableType) {
    addOccurrenceItem(
      items,
      createTagListItem(
        t('WORD.TAG'),
        metricCondition.tags
          ?.filter((tag) => tag.tagName || tag.tagId === '*')
          .map(convertTagToDisplayFormat),
      ),
    );
    addOccurrenceItem(
      items,
      createTargetListItem(
        t('WORD.TARGET'),
        metricCondition.targets
          ?.filter((target) => target.targetName || target.targetAlias)
          .map(convertTargetToDisplayFormat),
      ),
    );
  }
  addOccurrenceItem(
    items,
    createNotificationStatusItem(t('WORD.NOTIFICATION_STATUS'), notificationStatus),
  );

  addOccurrenceItem(items, createStatusItem(t('WORD.ALERT_LEVEL'), metricCondition.alertLevel));

  // Table 타입 특화 정보
  if (isTableType) {
    addOccurrenceItem(
      items,
      createAlertFilterItem(
        `${t('WORD.FILTER')} ${t('WORD.COLUMNS')}`,
        metricCondition.alertFilter,
      ),
    );

    addOccurrenceItem(
      items,
      createDisplayColumnsItem(
        t('WORD.NOTIFICATION_DISPLAY_COLUMNS'),
        metricCondition.displayColumns,
      ),
    );
  }
};

// 발생 메시지 아이템 생성 함수
export const buildOccurrenceItems = (
  messageItem: EventAlertMessageItem | undefined,
  eventConditions: ResponseEventConditionV9[],
  metricConditions: ResponseMetricConditionV9[],
  systemCondition: ResponseSystemCondition | undefined,
  t: (key: string) => string,
  alertRuleType?: string,
  ruleCriteria?: string,
  getReasons?: (eventIds: number[]) => string[],
  getStatName?: (category: string, statId: string) => string,
  notificationStatus?: NotificationMethod[],
  getAlertValue?: AlertValueFormatter,
): OccurrenceItem[] => {
  const items: OccurrenceItem[] = [];

  // 공통 정보 추가
  addCommonOccurrenceItems(items, messageItem, t);

  // System 조건 처리
  if (systemCondition) {
    addSystemConditionItems(items, systemCondition, t, notificationStatus);
  }

  // 이벤트 조건 처리
  if (eventConditions.length > 0) {
    addEventConditionItems(
      items,
      eventConditions[0],
      t,
      alertRuleType,
      getReasons,
      notificationStatus,
      getAlertValue,
    );
  }

  // 메트릭 조건 처리
  if (metricConditions.length > 0) {
    addMetricConditionItems(
      items,
      metricConditions[0],
      t,
      alertRuleType,
      ruleCriteria,
      getStatName,
      notificationStatus,
    );
  }

  return items;
};

// Path 그리드 데이터 변환 함수
export const convertLoggingConditionToPathRows = (
  loggingCondition?: LoggingCondition,
): unknown[][] => {
  if (!loggingCondition) {
    return [];
  }

  return [
    [
      loggingCondition.pathName ?? '',
      loggingCondition.logType ?? '',
      loggingCondition.filePath ?? '',
      loggingCondition.filterDetailValues ?? [],
      loggingCondition.logParserItems?.map((parser) => parser.parserType ?? '') ?? [],
    ],
  ];
};
